/**
 * Module dependencies.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownLeft, ArrowUpRight, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import moment from 'moment';
import { Badge } from 'app/components/ui/badge';
import { Spinner } from 'app/components/ui/spinner';
import { cn } from 'app/lib/utils';
import {
  CgAssetRewardRun,
  CgAssetTxEntry,
  useCryptoGestAssetRewardRun,
  useCryptoGestAssetTransactions
} from 'app/hooks/requests/crypto-gest/use-crypto-gest';
import { PiCgLink, usePoolInvestCryptoGestLinks } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { useFeature } from 'app/hooks/requests/use-feature-flags';
import { amount, formatInCurrency, pct, signClass } from '../format';
import { useDisplayMoney } from '../use-display-money';
import { PoolAllocationCell } from '../transactions/crypto-gest-pool-allocation';

/**
 * The columns, in order. `right`-aligned ones are numeric.
 */

const COLUMNS: Array<{ key: string; labelKey: string; right?: boolean }> = [
  { key: 'date', labelKey: 'cryptoGest.form.date' },
  { key: 'type', labelKey: 'cryptoGest.form.entryType' },
  { key: 'amount', labelKey: 'cryptoGest.labels.amount', right: true },
  { key: 'unitPrice', labelKey: 'cryptoGest.form.unitPrice', right: true },
  { key: 'totalSpent', labelKey: 'cryptoGest.transactions.totalSpent', right: true },
  { key: 'currentPrice', labelKey: 'cryptoGest.transactions.currentPrice', right: true },
  { key: 'profit', labelKey: 'cryptoGest.transactions.profitValue', right: true },
  { key: 'actions', labelKey: '' }
];

/**
 * Props.
 */

type Props = {
  assetId: string | number;
  walletId: string;
  symbol: string;
  types: string[] | null;
  includePool: boolean;
  currentPriceUsd: number | null;
  pageSize: number;
};

/**
 * Export `AssetWalletTransactions` — the paginated, server-filtered ledger for a
 * single asset in a single wallet, with contiguous reward runs folded into
 * lazily-expandable rows.
 */

export function AssetWalletTransactions({
  assetId,
  walletId,
  symbol,
  types,
  includePool,
  currentPriceUsd,
  pageSize
}: Props): JSX.Element {
  const [translate] = useTranslation();
  const { format: usd, usdPerEur } = useDisplayMoney();
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Filters/page-size changes restart from the first page.
  useEffect(() => {
    setPage(0);
    setExpanded(new Set());
  }, [types, includePool, pageSize, walletId]);

  const { data, isLoading, isFetching } = useCryptoGestAssetTransactions(assetId, {
    walletId,
    types,
    includePool,
    page,
    pageSize
  });

  const hasPool = useFeature('poolInvest');
  const { data: cgLinks } = usePoolInvestCryptoGestLinks();
  const linksBySource = useMemo(() => {
    const map: Record<string, PiCgLink[]> = {};

    (cgLinks ?? []).forEach(link => {
      (map[link.sourceEntryId] ??= []).push(link);
    });

    return map;
  }, [cgLinks]);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = data?.pageCount ?? 1;

  const toggleRun = (key: string) =>
    setExpanded(previous => {
      const next = new Set(previous);

      next.has(key) ? next.delete(key) : next.add(key);

      return next;
    });

  // The cells for a single entry row (reused by top-level rows and run members).
  const entryCells = (row: CgAssetTxEntry) => {
    const links = linksBySource[row.id] ?? [];
    const profit = (() => {
      if (row.type !== 'buy' || !row.unitPriceFiat || currentPriceUsd == null) {
        return null;
      }

      const costUsd = parseFloat(row.unitPriceFiat) * (row.currency === 'EUR' ? usdPerEur : 1);

      if (!(costUsd > 0)) {
        return null;
      }

      return {
        usd: (currentPriceUsd - costUsd) * parseFloat(row.amount),
        pct: (currentPriceUsd / costUsd - 1) * 100
      };
    })();

    return (
      <>
        <td className={'whitespace-nowrap px-3 py-2 text-xs text-muted-foreground'}>
          {moment(row.timestamp).format('DD MMM YYYY, HH:mm')}
        </td>

        <td className={'whitespace-nowrap px-3 py-2'}>
          <span className={'inline-flex items-center gap-1.5 text-foreground'}>
            {row.direction === 'in' ? (
              <ArrowDownLeft className={'h-3.5 w-3.5 text-[#1B752B]'} />
            ) : (
              <ArrowUpRight className={'h-3.5 w-3.5 text-destructive'} />
            )}

            {translate(`cryptoGest.entryType.${row.type}`)}

            {row.needsData && (
              <Badge className={'border-amber-500/40 bg-amber-500/10 text-amber-700'} variant={'outline'}>
                {translate('cryptoGest.transactions.missingData')}
              </Badge>
            )}

            {links.map(link => (
              <span
                className={
                  'inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#c9d8f2] bg-[#eef2fb] px-2 py-0.5 text-[11px] font-medium text-[#2f5c9f]'
                }
                key={link.projectId}
              >
                {link.projectName}
              </span>
            ))}
          </span>
        </td>

        <td className={'whitespace-nowrap px-3 py-2 text-right tabular-nums'}>
          {amount(row.amount)} {symbol}
        </td>

        <td className={'whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground'}>
          {row.unitPriceFiat ? formatInCurrency(parseFloat(row.unitPriceFiat), row.currency) : '—'}
        </td>

        <td className={'whitespace-nowrap px-3 py-2 text-right tabular-nums'}>
          {row.totalSpent ? formatInCurrency(parseFloat(row.totalSpent), row.currency) : '—'}
        </td>

        <td className={'whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground'}>
          {row.type === 'buy' && currentPriceUsd != null ? usd(currentPriceUsd) : '—'}
        </td>

        <td className={'whitespace-nowrap px-3 py-2 text-right tabular-nums'}>
          {profit ? (
            <span className={`flex flex-col items-end leading-tight ${signClass(profit.usd)}`}>
              <span>{usd(profit.usd)}</span>

              <span className={'text-xs'}>{pct(profit.pct)}</span>
            </span>
          ) : (
            '—'
          )}
        </td>

        <td className={'px-3 py-2 text-right'}>
          {hasPool ? (
            <div className={'flex items-center justify-end'}>
              <PoolAllocationCell
                links={links}
                tx={{
                  id: row.id,
                  symbol,
                  amount: row.amount,
                  unitPriceFiat: row.unitPriceFiat,
                  currency: row.currency,
                  timestamp: row.timestamp,
                  direction: row.direction
                }}
              />
            </div>
          ) : null}
        </td>
      </>
    );
  };

  // The summary row for a folded reward run (click to expand its members).
  const runRow = (run: CgAssetRewardRun) => {
    const key = `${run.from}|${run.to}`;
    const isOpen = expanded.has(key);
    const from = moment(run.from).format('DD MMM YYYY');
    const to = moment(run.to).format('DD MMM YYYY');

    return (
      <React.Fragment key={key}>
        <tr className={'cursor-pointer bg-muted/30 font-medium hover:bg-muted/50'} onClick={() => toggleRun(key)}>
          <td className={'whitespace-nowrap px-3 py-2 text-xs text-muted-foreground'}>
            <span className={'inline-flex items-center gap-1.5'}>
              {isOpen ? (
                <ChevronDown className={'h-3.5 w-3.5 shrink-0 text-muted-foreground'} />
              ) : (
                <ChevronRight className={'h-3.5 w-3.5 shrink-0 text-muted-foreground'} />
              )}

              {translate('cryptoGest.group.range', { from, to })}
            </span>
          </td>

          <td className={'whitespace-nowrap px-3 py-2'}>
            <span className={'inline-flex items-center gap-1.5 text-foreground'}>
              <ArrowDownLeft className={'h-3.5 w-3.5 text-[#1B752B]'} />

              {translate('cryptoGest.labels.rewards')}

              <span className={'rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground'}>
                {translate('cryptoGest.group.rewards', { count: run.count })}
              </span>
            </span>
          </td>

          <td className={'whitespace-nowrap px-3 py-2 text-right tabular-nums'}>
            {amount(run.amount)} {symbol}
          </td>

          <td className={'px-3 py-2 text-right text-muted-foreground'}>{'—'}</td>
          <td className={'px-3 py-2 text-right text-muted-foreground'}>{'—'}</td>
          <td className={'px-3 py-2 text-right text-muted-foreground'}>{'—'}</td>
          <td className={'px-3 py-2 text-right text-muted-foreground'}>{'—'}</td>
          <td className={'px-3 py-2'} />
        </tr>

        {isOpen && (
          <RewardRunRows
            assetId={assetId}
            from={run.from}
            includePool={includePool}
            renderCells={entryCells}
            to={run.to}
            walletId={walletId}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <div>
      <div className={'overflow-x-auto'}>
        <table className={'w-full border-collapse text-sm'}>
          <thead>
            <tr>
              {COLUMNS.map(column => (
                <th
                  className={cn(
                    'border-b-2 border-[#e2e5e9] bg-[#eef1f4] px-3 py-3 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[#334155]',
                    column.right && 'text-right'
                  )}
                  key={column.key}
                >
                  {column.labelKey ? translate(column.labelKey) : ''}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className={'divide-y divide-border'}>
            {isLoading ? (
              <tr>
                <td className={'px-3 py-10'} colSpan={COLUMNS.length}>
                  <div className={'grid place-items-center'}>
                    <Spinner />
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className={'px-3 py-10 text-center text-sm text-muted-foreground'} colSpan={COLUMNS.length}>
                  {translate('cryptoGest.transactions.noResults')}
                </td>
              </tr>
            ) : (
              rows.map(row =>
                row.kind === 'rewardRun' ? (
                  runRow(row)
                ) : (
                  <tr className={cn('hover:bg-muted/40', row.poolAssigned && 'opacity-50')} key={row.id}>
                    {entryCells(row)}
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div
          className={
            'flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-xs text-muted-foreground'
          }
        >
          <span>
            {total === 0 ? 0 : page * pageSize + 1}
            {'–'}
            {Math.min((page + 1) * pageSize, total)}
            {' / '}
            {total}
            {isFetching ? ' …' : ''}
          </span>

          <div className={'flex items-center gap-1'}>
            <button
              className={
                'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border transition-colors hover:bg-muted/40 disabled:opacity-40'
              }
              disabled={page <= 0}
              onClick={() => setPage(current => Math.max(0, current - 1))}
              type={'button'}
            >
              <ChevronLeft className={'h-4 w-4'} />
            </button>

            <span className={'px-1'}>
              {page + 1}
              {' / '}
              {pageCount}
            </span>

            <button
              className={
                'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border transition-colors hover:bg-muted/40 disabled:opacity-40'
              }
              disabled={page >= pageCount - 1}
              onClick={() => setPage(current => Math.min(pageCount - 1, current + 1))}
              type={'button'}
            >
              <ChevronRight className={'h-4 w-4'} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The individual reward payouts inside one folded run — fetched on demand.
 */

function RewardRunRows({
  assetId,
  walletId,
  from,
  to,
  includePool,
  renderCells
}: {
  assetId: string | number;
  walletId: string;
  from: string;
  to: string;
  includePool: boolean;
  renderCells: (row: CgAssetTxEntry) => JSX.Element;
}): JSX.Element {
  const { data, isLoading } = useCryptoGestAssetRewardRun(assetId, { walletId, from, to, includePool }, true);

  if (isLoading) {
    return (
      <tr className={'bg-muted/10'}>
        <td className={'px-3 py-4'} colSpan={COLUMNS.length}>
          <div className={'grid place-items-center'}>
            <Spinner />
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      {(data?.rows ?? []).map(row => (
        <tr className={'bg-muted/10'} key={row.id}>
          {renderCells(row)}
        </tr>
      ))}
    </>
  );
}
